/**
 * @x9/routine-run-model — canonical routine-run state machine (redesign 2026-07).
 *
 * A run is ONE `routineRuns/{runId}` document with an embedded `steps[]` array —
 * no per-step task-doc mirrors (see docs/meta/prd-task-model-redesign-2026-07.md).
 * This module is the single source of truth for:
 *
 *   - the run/step status vocabulary (execute funnel + check yes/no funnel)
 *   - `applyRunAction()` — a PURE reducer that validates and applies every
 *     lifecycle action (accept, start, step resolution, reopen, reassign,
 *     release, archive) and returns the next run doc
 *   - `deriveRunState()` / `computeRunProgress()` — composite state + progress
 *
 * The `advanceRunStep` Cloud Function callable runs this reducer with Admin
 * writes (authoritative), and the mobile client runs the SAME reducer to apply
 * the action optimistically offline before the queued callable confirms it.
 * Because both sides execute identical pure logic, an accepted offline action
 * converges to the server result. Dependency-free CommonJS so it loads in Node
 * (functions/Jest), Metro (React Native), and Vite without transpilation.
 */

'use strict';

/** Composite run states. `flagged` = all work resolved but ≥1 negative outcome. */
const RUN_STATES = Object.freeze({
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  IN_PROGRESS: 'inProgress',
  // Stopped mid-execution WITH resolved work kept (derived structurally:
  // accepted + resolved steps). A stop with zero work returns to ACCEPTED.
  PAUSED: 'paused',
  // Executor declined a pending run; stays assigned, owner resolves
  // (reassign / delete) — mirrors the task denial flow.
  DENIED: 'denied',
  COMPLETED: 'completed',
  FLAGGED: 'flagged',
});

/** Per-step resolution models (chosen by the creator, per step). */
const RESOLUTION_MODES = Object.freeze({
  EXECUTE: 'execute',
  CHECK: 'check',
});

/** All persisted step statuses across both resolution modes. */
const STEP_STATUSES = Object.freeze([
  'pending', 'inProgress', 'completed', 'denied', // execute funnel
  'checkedYes', 'checkedNo',                      // check funnel
]);

/** Step statuses that count as RESOLVED (no further executor work). */
const RESOLVED_STEP_STATUSES = Object.freeze([
  'completed', 'denied', 'checkedYes', 'checkedNo',
]);

/** Step statuses that flag the run for owner review once everything resolves. */
const NEGATIVE_STEP_STATUSES = Object.freeze(['denied', 'checkedNo']);

/** Clean resolutions that earn full step points (PRD scoring D5). */
const CLEAN_STEP_STATUSES = Object.freeze(['completed', 'checkedYes']);

/** Inclusive points range for routine steps (PRD D1). */
const STEP_POINTS_MIN = 1;
const STEP_POINTS_MAX = 10;
const DEFAULT_STEP_POINTS = 1;

/** Action types accepted by applyRunAction / the advanceRunStep callable. */
const RUN_ACTIONS = Object.freeze({
  ACCEPT: 'accept',                // executor: pending → accepted
  UNACCEPT: 'unaccept',            // executor: accepted → pending (keeps assignment)
  DENY: 'deny',                    // executor: pending → denied (keeps assignment; owner resolves)
  START: 'start',                  // executor: accepted → inProgress (resume / explicit); first-step resolve also starts
  STOP: 'stop',                    // executor: inProgress → accepted (de-arms steps, keeps resolved)
  COMPLETE_STEP: 'completeStep',   // executor: execute step → completed (proof/note)
  DENY_STEP: 'denyStep',           // executor: execute step → denied
  CHECK_STEP: 'checkStep',         // executor: check step → checkedYes | checkedNo
  REOPEN_STEP: 'reopenStep',       // owner or executor: resolved step → pending
  EDIT_STEP_NOTE: 'editStepNote',  // author: revise the comment on their own resolved step
  REASSIGN: 'reassign',            // owner: swap executor, reset non-terminal steps
  RELEASE: 'release',              // executor: hand run back (unassigned)
  ARCHIVE: 'archive',              // owner: hide a settled run
  UNARCHIVE: 'unarchive',          // owner: restore an archived run to the active set
});

function isStepResolved(step) {
  return !!step && RESOLVED_STEP_STATUSES.includes(step.status);
}

function stepMode(step) {
  return step && step.resolutionMode === RESOLUTION_MODES.CHECK
    ? RESOLUTION_MODES.CHECK
    : RESOLUTION_MODES.EXECUTE;
}

/**
 * Whether a step gates run completion. Execute steps may opt out with
 * `requireProof: false` (optional steps, legacy semantics); check steps always
 * gate — a yes/no verification is the point of the step.
 */
function isGatingStep(step) {
  if (stepMode(step) === RESOLUTION_MODES.CHECK) return true;
  return !step || step.requireProof !== false;
}

/** Typed error the callable maps onto HttpsError codes. */
function runActionError(code, message) {
  const err = new Error(message);
  err.code = code; // 'permission-denied' | 'failed-precondition' | 'invalid-argument' | 'not-found'
  err.isRunActionError = true;
  return err;
}

/**
 * Derive the composite run state from lifecycle stamps + step statuses.
 * Mid-run negative outcomes do NOT block or flag early (locked decision #5:
 * record + continue); `failedCheckCount`/badges surface them while running.
 *
 * @param {Object} run - run doc ({ steps, acceptedAt, startedAt })
 * @returns {string} one of RUN_STATES
 */
function deriveRunState(run) {
  const steps = Array.isArray(run && run.steps) ? run.steps.filter(Boolean) : [];
  if (steps.length > 0) {
    const gating = steps.filter(isGatingStep);
    const gate = gating.length > 0 ? gating : steps;
    if (gate.every(isStepResolved)) {
      const anyNegative = steps.some((s) => NEGATIVE_STEP_STATUSES.includes(s.status));
      return anyNegative ? RUN_STATES.FLAGGED : RUN_STATES.COMPLETED;
    }
  }
  // Executor declined the run while pending. The stamp is cleared when the
  // owner reassigns, so DENIED can never coexist with lifecycle progress.
  if (run && run.deniedAt) return RUN_STATES.DENIED;
  // Lifecycle timestamps take precedence over step statuses: a STOPPED run
  // must not read as inProgress. With work already resolved it reads as
  // PAUSED (structural: accepted + resolved steps — a stop with zero work
  // simply returns to accepted, so "Aceitas" always means "not yet worked").
  if (run && run.startedAt) return RUN_STATES.IN_PROGRESS;
  if (run && run.acceptedAt) {
    return steps.some(isStepResolved) ? RUN_STATES.PAUSED : RUN_STATES.ACCEPTED;
  }
  if (steps.length > 0 && steps.some((s) => !isStepResolved(s) && s.status !== 'pending')) {
    // Armed-but-unresolved steps without lifecycle stamps: legacy docs from
    // before the stamps existed — execution is genuinely underway.
    return RUN_STATES.IN_PROGRESS;
  }
  // Everything else without stamps reads as PENDING — including runs that
  // carry resolved work from a previous executor (REASSIGN keeps outcomes
  // but the new executor must accept from scratch) and any contradictory
  // stranded doc (pending is always reachable: accept works from here).
  return RUN_STATES.PENDING;
}

/** Count of steps resolved negatively via check mode (`checkedNo`). */
function countFailedChecks(steps) {
  return (Array.isArray(steps) ? steps : []).filter(
    (s) => s && s.status === 'checkedNo',
  ).length;
}

/**
 * Resolved-vs-total progress over completion-gating steps.
 * @returns {{completed:number,total:number,ratio:number}}
 */
function computeRunProgress(steps) {
  const list = Array.isArray(steps) ? steps.filter(Boolean) : [];
  const gating = list.filter(isGatingStep);
  const gate = gating.length > 0 ? gating : list;
  const total = gate.length;
  const completed = gate.filter(isStepResolved).length;
  return { completed, total, ratio: total > 0 ? completed / total : 0 };
}

/** True when nothing is left for the executor to act on. */
function isRunSettled(run) {
  const state = deriveRunState(run);
  return state === RUN_STATES.COMPLETED || state === RUN_STATES.FLAGGED;
}

/** Clamp / coerce a step points value into the legal 1–10 integer range. */
function clampStepPoints(value, fallback = DEFAULT_STEP_POINTS) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(STEP_POINTS_MAX, Math.max(STEP_POINTS_MIN, Math.round(n)));
}

/** Points earned for one step (full weight on clean outcome, else 0). */
function stepPointsEarned(step) {
  if (!step || !CLEAN_STEP_STATUSES.includes(step.status)) return 0;
  return clampStepPoints(step.points);
}

/**
 * Build denormalized section roll-ups + run totals from steps (PRD scoring).
 * Reusable by run UI, stats, and PDF. Unresolved / negative steps earn 0.
 *
 * @param {Array<Object>} steps
 * @param {Array<{sectionId,name,order,pointsPossible}>} [sectionsSnapshot]
 * @returns {{
 *   pointsPossible:number,
 *   pointsEarned:number,
 *   ncCount:number,
 *   unresolvedCount:number,
 *   scoreRatio:number|null,
 *   sections: Array<{
 *     sectionId:string|null,
 *     name:string|null,
 *     order:number,
 *     pointsPossible:number,
 *     pointsEarned:number,
 *     ncCount:number,
 *     unresolvedCount:number,
 *     scoreRatio:number|null,
 *   }>
 * }}
 */
function computeRunScore(steps, sectionsSnapshot) {
  const list = Array.isArray(steps) ? steps.filter(Boolean) : [];
  const byId = new Map();

  const ensureSection = (sectionId, name, order) => {
    const key = sectionId || '__none__';
    if (!byId.has(key)) {
      byId.set(key, {
        sectionId: sectionId || null,
        name: typeof name === 'string' ? name : null,
        order: Number.isFinite(order) ? order : Number.MAX_SAFE_INTEGER,
        pointsPossible: 0,
        pointsEarned: 0,
        ncCount: 0,
        unresolvedCount: 0,
        scoreRatio: null,
      });
    }
    return byId.get(key);
  };

  if (Array.isArray(sectionsSnapshot)) {
    for (const snap of sectionsSnapshot) {
      if (!snap || !snap.sectionId) continue;
      ensureSection(snap.sectionId, snap.name, snap.order);
    }
  }

  let pointsPossible = 0;
  let pointsEarned = 0;
  let ncCount = 0;
  let unresolvedCount = 0;

  for (let i = 0; i < list.length; i += 1) {
    const step = list[i];
    const pts = clampStepPoints(step.points);
    const section = ensureSection(
      step.sectionId || null,
      step.sectionName || null,
      Number.isFinite(step.sectionOrder) ? step.sectionOrder : i,
    );
    if (section.name == null && typeof step.sectionName === 'string') {
      section.name = step.sectionName;
    }
    if (section.order === Number.MAX_SAFE_INTEGER && Number.isFinite(step.order)) {
      section.order = step.order;
    }

    pointsPossible += pts;
    section.pointsPossible += pts;

    const earned = stepPointsEarned(step);
    pointsEarned += earned;
    section.pointsEarned += earned;

    if (NEGATIVE_STEP_STATUSES.includes(step.status)) {
      ncCount += 1;
      section.ncCount += 1;
    } else if (!isStepResolved(step)) {
      unresolvedCount += 1;
      section.unresolvedCount += 1;
    }
  }

  const sections = [...byId.values()]
    .sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return String(a.sectionId || '').localeCompare(String(b.sectionId || ''));
    })
    .map((s) => ({
      ...s,
      scoreRatio: s.pointsPossible > 0 ? s.pointsEarned / s.pointsPossible : null,
    }));

  return {
    pointsPossible,
    pointsEarned,
    ncCount,
    unresolvedCount,
    scoreRatio: pointsPossible > 0 ? pointsEarned / pointsPossible : null,
    sections,
  };
}

/**
 * Snapshot section metadata + possible points at dispatch (PRD O4).
 * Prefer explicit `sections` from the template bucket; otherwise derive from steps.
 */
function buildSectionsSnapshot(steps, templateSections) {
  const list = Array.isArray(steps) ? steps.filter(Boolean) : [];
  if (Array.isArray(templateSections) && templateSections.length > 0) {
    return templateSections
      .filter((s) => s && s.sectionId)
      .map((s, i) => {
        const sectionId = s.sectionId;
        const pointsPossible = list
          .filter((st) => st.sectionId === sectionId)
          .reduce((sum, st) => sum + clampStepPoints(st.points), 0);
        return {
          sectionId,
          name: typeof s.name === 'string' ? s.name : null,
          order: Number.isFinite(s.order) ? s.order : i,
          pointsPossible,
        };
      })
      .sort((a, b) => a.order - b.order);
  }

  // Derive ordered unique sections from frozen step fields.
  const seen = new Map();
  for (const step of list) {
    const id = step.sectionId || null;
    if (!id || seen.has(id)) continue;
    seen.set(id, {
      sectionId: id,
      name: typeof step.sectionName === 'string' ? step.sectionName : null,
      order: seen.size,
      pointsPossible: 0,
    });
  }
  for (const step of list) {
    if (!step.sectionId || !seen.has(step.sectionId)) continue;
    seen.get(step.sectionId).pointsPossible += clampStepPoints(step.points);
  }
  return [...seen.values()];
}

/**
 * Build a run step from prepared template-step fields. Content resolution
 * (title/description/media selection from the template task) is the caller's
 * concern; this normalizes shape and stamps the initial status.
 */
function buildRunStep(fields, order) {
  const src = fields || {};
  return {
    stepId: src.stepId || `step_${order}_${Math.random().toString(36).slice(2, 8)}`,
    originTemplateTaskId: src.originTemplateTaskId || null,
    order: Number.isFinite(order) ? order : (Number.isFinite(src.order) ? src.order : 0),
    title: typeof src.title === 'string' ? src.title : '',
    description: typeof src.description === 'string' ? src.description : '',
    resolutionMode: stepMode(src),
    requireProof: src.requireProof !== false,
    // Check mode: per-outcome evidence. No-side defaults on (legacy); yes-side
    // defaults off so existing templates keep instant-yes until authors opt in.
    checkRequireNote: src.checkRequireNote !== false,
    checkRequirePhoto: src.checkRequirePhoto !== false,
    checkYesRequireNote: src.checkYesRequireNote === true,
    checkYesRequirePhoto: src.checkYesRequirePhoto === true,
    // Frozen weight + section membership (PRD G4) — template edits after
    // dispatch never rewrite these.
    points: clampStepPoints(src.points),
    sectionId: typeof src.sectionId === 'string' && src.sectionId ? src.sectionId : null,
    sectionName: typeof src.sectionName === 'string' && src.sectionName ? src.sectionName : null,
    // Durable remote media references only (offline-first: devices hydrate
    // their own cache; local file:// paths must never be embedded).
    photoUri: src.photoUri || null,
    thumbnailUri: src.thumbnailUri || null,
    thumbnailUrl: src.thumbnailUrl || null,
    mediumUrl: src.mediumUrl || null,
    compressedUrl: src.compressedUrl || null,
    audioUrl: src.audioUrl || null,
    audioDuration: src.audioDuration || null,
    // Either a pre-serialized JSON string (Firestore-safe — nested point
    // arrays are rejected as raw values) or an already-flat array.
    drawingPaths:
      (typeof src.drawingPaths === 'string' && src.drawingPaths) ||
      (Array.isArray(src.drawingPaths) ? src.drawingPaths : null),
    highlightSvg: typeof src.highlightSvg === 'string' && src.highlightSvg ? src.highlightSvg : null,
    hasOverlay: src.hasOverlay === true,
    location: src.location || null,
    status: 'pending',
    outcome: null,       // check mode: 'yes' | 'no'
    proofUrls: [],       // Storage refs (never embedded blobs)
    details: null,       // comment text when required by outcome config
    note: null,          // execute-mode completion note
    // Offline execution note captured as AUDIO, pending transcription. Holds
    // { uri, url, storagePath, durationSeconds, mimeType, state, textField }
    // and is DELETED once its transcript lands in note/details.
    //
    // Deliberately NOT named audioUrl/audioUri/audioDuration: those already
    // exist on a step and mean "the audio this step was CREATED from" (a
    // template step cloned from an audio capture). Conflating authoring
    // evidence with execution evidence would corrupt both.
    voiceNote: null,
    completedAt: null,
    resolvedBy: null,
  };
}

/** Owner and executor are the same person — assign already implies accept. */
function isSelfAssignedRun(run) {
  return !!(run && run.ownerUserId && run.executorUserId && run.ownerUserId === run.executorUserId);
}

/**
 * Build the single routineRuns/{runId} doc (replaces per-step mirrors).
 */
function buildRunDoc(params) {
  const p = params || {};
  const nowIso = p.nowIso || new Date().toISOString();
  const steps = (Array.isArray(p.steps) ? p.steps : []).map((s, i) => buildRunStep(s, i));
  const sectionsSnapshot = buildSectionsSnapshot(steps, p.sections);
  const pointsPossible = steps.reduce((sum, s) => sum + clampStepPoints(s.points), 0);
  const selfAssigned = isSelfAssignedRun(p);
  return {
    runId: p.runId,
    schemaVersion: 2, // steps-array model; v1 = mirror-task model
    bucketId: p.bucketId || null,
    bucketName: typeof p.bucketName === 'string' ? p.bucketName : null,
    ownerUserId: p.ownerUserId || null,
    executorContactId: p.executorContactId || null,
    executorUserId: p.executorUserId || null,
    templateVersion: Number.isFinite(p.templateVersion) ? p.templateVersion : 1,
    state: selfAssigned ? RUN_STATES.ACCEPTED : RUN_STATES.PENDING,
    failedCheckCount: 0,
    archived: false,
    archivedAt: null,
    archivedBy: null,
    // Per-perspective archive flags (prd-task-run-archiving-2026-08, D1.2
    // applied to runs): each party hides the run from their OWN view;
    // `archived` is DERIVED (promoted when both perspectives are archived,
    // or when there is no foreign executor).
    ownerArchived: false,
    ownerArchivedAt: null,
    executorArchived: false,
    executorArchivedAt: null,
    createdAt: nowIso,
    updatedAt: nowIso,
    // Self-dispatch skips the accept handshake — the stamp equals createdAt
    // so lists land on Aceitas and the run screen can show Created instead.
    acceptedAt: selfAssigned ? nowIso : null,
    startedAt: null,
    deniedAt: null,
    completedAt: null,
    pointsPossible,
    sectionsSnapshot,
    steps,
    history: [],
  };
}

function findStepIndex(run, stepId) {
  const steps = Array.isArray(run.steps) ? run.steps : [];
  return steps.findIndex((s) => s && s.stepId === stepId);
}

/** History events that carry a step's resolution comment. */
const STEP_RESOLUTION_EVENTS = Object.freeze(['step_completed', 'step_denied', 'step_checked']);

/**
 * Index of the event holding a step's CURRENT resolution comment.
 *
 * The status timeline renders history events, not the step fields, so an
 * amendment has to land on the live resolution event or the edit would be
 * invisible. Earlier events belong to resolutions a reopen superseded and stay
 * as they were recorded.
 */
function findLatestResolutionEventIndex(history, stepId) {
  if (!Array.isArray(history)) return -1;
  for (let i = history.length - 1; i >= 0; i -= 1) {
    const ev = history[i];
    if (ev && ev.stepId === stepId && STEP_RESOLUTION_EVENTS.includes(ev.type)) return i;
  }
  return -1;
}

function requireExecutor(ctx, what) {
  if (!ctx.isExecutor) {
    throw runActionError('permission-denied', `Only the run executor may ${what}`);
  }
}

function requireOwner(ctx, what) {
  if (!ctx.isOwner) {
    throw runActionError('permission-denied', `Only the run owner may ${what}`);
  }
}

/**
 * Derived global archive (D1.2 for runs): promote `archived: true` when the
 * owner archived their perspective AND the executor perspective is settled
 * too (executor archived it, no executor, or a self-run). Runs mutate through
 * this model inside the advanceRunStep transaction, so — unlike tasks, which
 * need the onTaskUpdated trigger — promotion happens synchronously here.
 */
/**
 * SINGLE authority for the run archive-promotion rule
 * (prd-workaround-remediation-2026-08, W8/D3): the derived global `archived`
 * flag promotes to true when the owner archived their perspective AND the
 * executor perspective is settled (executor archived too, or there is no
 * foreign executor). Pure predicate — both writers consume it: the reducer
 * below (interactive advanceRunStep writes) and the auto-archive scheduler
 * (functions/src/autoArchive.js), which composes its own update payload.
 *
 * @param {Object} run - effective run doc (flags already reflecting the write)
 * @returns {boolean} whether `archived` should promote to true
 */
function shouldPromoteRunArchive(run) {
  if (!run) return false;
  const hasForeignExecutor =
    !!run.executorUserId && run.executorUserId !== run.ownerUserId;
  return run.archived !== true
    && run.ownerArchived === true
    && (!hasForeignExecutor || run.executorArchived === true);
}

function applyDerivedRunArchive(next, nowIso) {
  if (shouldPromoteRunArchive(next)) {
    next.archived = true;
    next.archivedAt = nowIso;
    next.archivedBy = 'system:archive-promotion';
  }
}

/**
 * Accept a voice note off an action, keeping ONLY the known fields.
 *
 * The reducer is the schema for a run doc — it copies named fields rather than
 * spreading client input, which is what stops a caller writing arbitrary data
 * into an audit record. A voice note has to follow the same discipline.
 *
 * `textField` records which text field the transcript belongs in, so the
 * server-side transcriber does not have to re-derive execute-vs-check from the
 * step: an execute step's note lives in `note`, a check step's in `details`.
 *
 * @param {Object} input     candidate voiceNote from the action
 * @param {string} textField 'note' | 'details'
 * @returns {Object|null}
 */
function normalizeVoiceNote(input, textField) {
  if (!input || typeof input !== 'object') return null;
  // Needs somewhere to read the audio from: a local file pre-upload, or a
  // Storage URL once the sync queue has uploaded it.
  if (!input.uri && !input.url) return null;
  return {
    uri: input.uri || null,
    url: input.url || null,
    storagePath: input.storagePath || null,
    durationSeconds: Number.isFinite(input.durationSeconds) ? input.durationSeconds : 0,
    mimeType: typeof input.mimeType === 'string' ? input.mimeType : 'audio/mp4',
    state: typeof input.state === 'string' ? input.state : 'recorded',
    recordedAt: input.recordedAt || null,
    attempts: Number.isFinite(input.attempts) ? input.attempts : 0,
    error: input.error || null,
    textField,
  };
}

/**
 * Validate and apply a run action, returning the NEXT run doc (pure — the
 * input run is not mutated). Throws runActionError on illegal actions.
 *
 * @param {Object} run - current routineRuns doc (schemaVersion 2)
 * @param {Object} action - { type, stepId?, outcome?, details?, proofUrls?, note?, voiceNote?, executorContactId?, executorUserId? }
 * @param {Object} ctx - { actorUserId, isOwner, isExecutor, nowIso? }
 * @returns {Object} next run doc
 */
function applyRunAction(run, action, ctx) {
  if (!run || !Array.isArray(run.steps)) {
    throw runActionError('not-found', 'Run document missing or malformed');
  }
  const a = action || {};
  const c = ctx || {};
  const nowIso = c.nowIso || new Date().toISOString();

  const next = {
    ...run,
    steps: run.steps.map((s) => ({ ...s })),
    history: Array.isArray(run.history) ? [...run.history] : [],
  };

  const pushEvent = (type, extra) => {
    next.history.push({
      type,
      actorUserId: c.actorUserId || null,
      timestamp: nowIso,
      ...(extra || {}),
    });
  };

  const getStep = () => {
    const idx = findStepIndex(next, a.stepId);
    if (idx < 0) throw runActionError('not-found', `Step not found: ${a.stepId}`);
    return next.steps[idx];
  };

  const armExecuteSteps = () => {
    for (const step of next.steps) {
      if (stepMode(step) === RESOLUTION_MODES.EXECUTE && step.status === 'pending') {
        step.status = 'inProgress';
      }
    }
  };

  const applyStartStamps = () => {
    next.startedAt = nowIso;
    armExecuteSteps();
    pushEvent('started');
  };

  // First Yes/No / complete joins Start: stamp startedAt when work begins.
  // Paused runs (resolved work, startedAt cleared) still need an explicit
  // START / Resume — do not start them from a later step.
  const ensureStartedForWork = (what) => {
    if (next.startedAt) return;
    if (next.deniedAt) {
      throw runActionError('failed-precondition', 'Run was denied — it must be reassigned first');
    }
    const hasResolvedWork = next.steps.some(isStepResolved);
    if (hasResolvedWork) {
      throw runActionError('failed-precondition', 'Run must be resumed before resolving more steps');
    }
    if (!next.acceptedAt) {
      if (!c.isOwner && !c.selfAssigned && !isSelfAssignedRun(next)) {
        throw runActionError('failed-precondition', 'Run must be accepted before resolving steps');
      }
      next.acceptedAt = nowIso;
      pushEvent('accepted');
    }
    applyStartStamps();
  };

  switch (a.type) {
    case RUN_ACTIONS.ACCEPT: {
      requireExecutor(c, 'accept the run');
      if (next.acceptedAt) break; // idempotent
      if (deriveRunState(next) !== RUN_STATES.PENDING) {
        throw runActionError('failed-precondition', 'Run is not pending acceptance');
      }
      next.acceptedAt = nowIso;
      pushEvent('accepted');
      break;
    }

    case RUN_ACTIONS.UNACCEPT: {
      // Inverse of ACCEPT (lifecycle reversal, 2026-08-07): the executor
      // backs out of acceptance but STAYS assigned — unlike RELEASE, which
      // hands the whole job back unassigned. One edge per action: a started
      // run must STOP first.
      requireExecutor(c, 'unaccept the run');
      if (!next.acceptedAt) break; // idempotent — already pending
      if (next.startedAt) {
        throw runActionError('failed-precondition', 'Run is started — stop it before unaccepting');
      }
      // A run that already carries resolved work cannot go back to "new" —
      // clearing acceptedAt while resolved steps remain leaves the doc
      // contradictory (pending by stamps, active by work) and it vanishes
      // from every list. Once work exists, backing out means STOP (stays
      // accepted) or RELEASE/ARCHIVE — never unaccept.
      if ((Array.isArray(next.steps) ? next.steps : []).some(isStepResolved)) {
        throw runActionError(
          'failed-precondition',
          'Run has resolved steps — it cannot return to new',
        );
      }
      next.acceptedAt = null;
      pushEvent('unaccepted');
      break;
    }

    case RUN_ACTIONS.DENY: {
      // Executor declines a pending run (parity with the task denial flow,
      // 2026-08-07): stays assigned so the owner keeps context, and the
      // owner resolves via REASSIGN (clears the stamp) or delete. Only a
      // pristine pending run can be denied — once accepted or worked, the
      // exits are UNACCEPT / STOP / RELEASE.
      requireExecutor(c, 'deny the run');
      if (next.deniedAt) break; // idempotent
      if (deriveRunState(next) !== RUN_STATES.PENDING) {
        throw runActionError('failed-precondition', 'Only a pending run can be denied');
      }
      next.deniedAt = nowIso;
      pushEvent('denied', a.note ? { note: a.note } : undefined);
      break;
    }

    case RUN_ACTIONS.START: {
      requireExecutor(c, 'start the run');
      if (next.startedAt) break; // idempotent
      if (next.deniedAt) {
        throw runActionError('failed-precondition', 'Run was denied — it must be reassigned first');
      }
      // Self-assigned executors may start directly from pending (accept is
      // auto-satisfied), matching the task pipeline's selfAssigned edge.
      if (!next.acceptedAt) {
        if (!c.isOwner && !c.selfAssigned) {
          throw runActionError('failed-precondition', 'Run must be accepted before start');
        }
        next.acceptedAt = nowIso;
        pushEvent('accepted');
      }
      applyStartStamps();
      break;
    }

    case RUN_ACTIONS.STOP: {
      // Inverse of START (lifecycle reversal, 2026-08-07): a pause, not an
      // undo — resolved steps keep status, proof, and attribution; only the
      // armed (inProgress) execute steps de-arm back to pending. The run
      // reads as `accepted` again; a later START re-arms remaining steps.
      requireExecutor(c, 'stop the run');
      if (!next.startedAt) break; // idempotent — not started
      if (isRunSettled(next)) {
        throw runActionError('failed-precondition', 'Run is settled — completed runs cannot be stopped');
      }
      next.startedAt = null;
      for (const step of next.steps) {
        if (stepMode(step) === RESOLUTION_MODES.EXECUTE && step.status === 'inProgress') {
          step.status = 'pending';
        }
      }
      pushEvent('stopped');
      break;
    }

    case RUN_ACTIONS.COMPLETE_STEP: {
      requireExecutor(c, 'complete a step');
      ensureStartedForWork('completing steps');
      const step = getStep();
      if (stepMode(step) !== RESOLUTION_MODES.EXECUTE) {
        throw runActionError('failed-precondition', 'checkStep must be used for check-mode steps');
      }
      if (step.status !== 'inProgress' && step.status !== 'pending') {
        throw runActionError('failed-precondition', `Cannot complete a step in status ${step.status}`);
      }
      step.status = 'completed';
      step.completedAt = nowIso;
      step.resolvedBy = c.actorUserId || null;
      if (typeof a.note === 'string' && a.note.trim()) step.note = a.note.trim();
      if (Array.isArray(a.proofUrls) && a.proofUrls.length > 0) step.proofUrls = a.proofUrls;
      step.voiceNote = normalizeVoiceNote(a.voiceNote, 'note');
      // Evidence is copied onto the event so it survives a later reopen
      // (which wipes the step-level fields) — the status timeline shows the
      // full resolve → reopen → re-resolve history with notes and proofs.
      pushEvent('step_completed', {
        stepId: step.stepId,
        note: step.note || null,
        proofUrls: Array.isArray(step.proofUrls) ? step.proofUrls : [],
        voiceNote: step.voiceNote,
      });
      break;
    }

    case RUN_ACTIONS.DENY_STEP: {
      requireExecutor(c, 'deny a step');
      ensureStartedForWork('denying steps');
      const step = getStep();
      if (stepMode(step) !== RESOLUTION_MODES.EXECUTE) {
        throw runActionError('failed-precondition', 'checkStep must be used for check-mode steps');
      }
      if (isStepResolved(step)) {
        throw runActionError('failed-precondition', `Cannot deny a step in status ${step.status}`);
      }
      step.status = 'denied';
      step.completedAt = nowIso;
      step.resolvedBy = c.actorUserId || null;
      if (typeof a.note === 'string' && a.note.trim()) step.note = a.note.trim();
      step.voiceNote = normalizeVoiceNote(a.voiceNote, 'note');
      pushEvent('step_denied', {
        stepId: step.stepId,
        note: step.note || null,
        voiceNote: step.voiceNote,
      });
      break;
    }

    case RUN_ACTIONS.CHECK_STEP: {
      requireExecutor(c, 'resolve a check step');
      ensureStartedForWork('resolving check steps');
      const step = getStep();
      if (stepMode(step) !== RESOLUTION_MODES.CHECK) {
        throw runActionError('failed-precondition', 'checkStep only applies to check-mode steps');
      }
      if (step.status !== 'pending') {
        throw runActionError('failed-precondition', `Check step already resolved (${step.status})`);
      }
      if (a.outcome !== 'yes' && a.outcome !== 'no') {
        throw runActionError('invalid-argument', "checkStep outcome must be 'yes' or 'no'");
      }
      const details = typeof a.details === 'string' ? a.details.trim() : '';
      const proofUrls = Array.isArray(a.proofUrls) ? a.proofUrls.filter(Boolean) : [];
      const voiceNote = normalizeVoiceNote(a.voiceNote, 'details');
      const requireNote = a.outcome === 'yes'
        ? step.checkYesRequireNote === true
        : step.checkRequireNote !== false;
      const requirePhoto = a.outcome === 'yes'
        ? step.checkYesRequirePhoto === true
        : step.checkRequirePhoto !== false;
      // A recorded voice note satisfies the note requirement: it IS the note,
      // just not yet transcribed. Without this an offline executor cannot
      // resolve a check step that demands a comment — the exact dead end this
      // feature exists to remove.
      if (requireNote && !details && !voiceNote) {
        throw runActionError(
          'invalid-argument',
          a.outcome === 'yes'
            ? "A 'yes' outcome requires details text"
            : "A 'no' outcome requires details text",
        );
      }
      if (requirePhoto && proofUrls.length === 0) {
        throw runActionError(
          'invalid-argument',
          a.outcome === 'yes'
            ? "A 'yes' outcome requires at least one proof photo"
            : "A 'no' outcome requires at least one proof photo",
        );
      }
      if (details) step.details = details;
      step.proofUrls = proofUrls;
      step.voiceNote = voiceNote;
      if (a.outcome === 'no') {
        step.status = 'checkedNo';
        step.outcome = 'no';
      } else {
        step.status = 'checkedYes';
        step.outcome = 'yes';
      }
      step.completedAt = nowIso;
      step.resolvedBy = c.actorUserId || null;
      pushEvent('step_checked', {
        stepId: step.stepId,
        outcome: a.outcome,
        details: step.details || null,
        proofUrls: Array.isArray(step.proofUrls) ? step.proofUrls : [],
        voiceNote: step.voiceNote,
      });
      break;
    }

    case RUN_ACTIONS.REOPEN_STEP: {
      if (!c.isOwner && !c.isExecutor) {
        throw runActionError('permission-denied', 'Only the run owner or executor may reopen a step');
      }
      const step = getStep();
      if (!isStepResolved(step)) {
        throw runActionError('failed-precondition', `Step is not resolved (${step.status})`);
      }
      // Back to the armed state for execute steps of a started run.
      step.status =
        stepMode(step) === RESOLUTION_MODES.EXECUTE && next.startedAt ? 'inProgress' : 'pending';
      step.outcome = null;
      step.details = null;
      step.proofUrls = [];
      step.note = null;
      // Clear the pending audio along with the rest of the evidence. Leaving it
      // would let an in-flight transcript land on a resolution that no longer
      // exists — the previous attempt's words reappearing after a reopen. The
      // resolution event keeps its own copy, so the history is intact.
      step.voiceNote = null;
      step.completedAt = null;
      step.resolvedBy = null;
      next.completedAt = null;
      // Optional owner note ("why am I reopening this?") — shown in the
      // step's status timeline like reopen comments on normal tasks.
      pushEvent('step_reopened', {
        stepId: step.stepId,
        note: typeof a.note === 'string' && a.note.trim() ? a.note.trim() : null,
        voiceNote: normalizeVoiceNote(a.voiceNote, 'note'),
      });
      break;
    }

    case RUN_ACTIONS.EDIT_STEP_NOTE: {
      // Revising your own comment is not a re-resolution: the outcome, proofs,
      // attribution and timestamps all stand. Before this, fixing a typo meant
      // reopening the step — which WIPES the evidence and makes the run look
      // like it failed and was redone.
      const step = getStep();
      if (!isStepResolved(step)) {
        throw runActionError('failed-precondition', 'Only a resolved step has a comment to revise');
      }
      // Its author may reword it, and so may the run owner: the report is the
      // owner's document to their client, and staff wording is sometimes
      // incomplete or plain wrong. An owner rewording someone else's account is
      // still a real risk, so the FIRST wording is kept on the resolution event
      // and the edit is marked as not the author's — the run doc always holds
      // enough to reconcile an exported report against what was written.
      // Runs recorded before `resolvedBy` existed fall back to the executor, who
      // is the only one who could have written it.
      const author = step.resolvedBy || null;
      const isAuthor = author
        ? author === c.actorUserId
        : c.isExecutor === true;
      if (!isAuthor && c.isOwner !== true) {
        throw runActionError(
          'permission-denied',
          'Only the author or the run owner may revise a step comment',
        );
      }
      const field = stepMode(step) === RESOLUTION_MODES.CHECK ? 'details' : 'note';
      const text = typeof a.note === 'string' ? a.note.trim() : '';
      // A check step whose template demands a comment must not be emptied —
      // that would leave a resolution the reducer would have refused to accept.
      if (!text && field === 'details') {
        const requireNote = step.outcome === 'yes'
          ? step.checkYesRequireNote === true
          : step.checkRequireNote !== false;
        if (requireNote) {
          throw runActionError('invalid-argument', 'This step requires a comment');
        }
      }
      // Only the text moves. The outcome, proofs, audio, attribution and
      // timestamps are the resolution itself, and rewording is not a
      // re-resolution — the export must keep showing the photos the executor
      // attached, whoever last touched the wording.
      const previousText = step[field] || null;
      step[field] = text || null;
      const evIdx = findLatestResolutionEventIndex(next.history, step.stepId);
      if (evIdx >= 0) {
        const event = next.history[evIdx];
        const originalKey = field === 'details' ? 'originalDetails' : 'originalNote';
        next.history[evIdx] = {
          ...event,
          [field]: text || null,
          // The FIRST wording, not the previous one: reconciliation is against
          // what the executor actually wrote, not against an intermediate draft.
          [originalKey]: Object.prototype.hasOwnProperty.call(event, originalKey)
            ? event[originalKey]
            : previousText,
          // Same marker normal-task timeline edits use, so the entry renders
          // as "(edited)" rather than silently changing under the reader.
          editedAt: nowIso,
          editedBy: c.actorUserId || null,
          // Someone rewording their own account is ordinary; someone rewording
          // another person's is what a reader needs to be told about.
          ...(isAuthor ? {} : { editedByOwner: true }),
        };
      }
      break;
    }

    case RUN_ACTIONS.REASSIGN: {
      requireOwner(c, 'reassign the run');
      next.executorContactId = a.executorContactId || null;
      next.executorUserId = a.executorUserId || null;
      // The new executor must not inherit the previous executor's archive
      // choice (D1.2 consistency — same rule as task reassignment); demote
      // the derived global flag with it.
      if (next.executorArchived === true) {
        next.executorArchived = false;
        next.executorArchivedAt = null;
        next.archived = false;
        next.archivedAt = null;
        next.archivedBy = null;
      }
      // A new executor must accept + start themselves; resolved steps keep
      // their outcome + attribution. Reassignment also resolves a denial —
      // the new executor gets a clean pending run. Self-reassign skips
      // accept (same as create): stamp equals createdAt so the UI can
      // show Created in the accepted-badge slot.
      next.acceptedAt = isSelfAssignedRun(next) ? (next.createdAt || nowIso) : null;
      next.startedAt = null;
      next.deniedAt = null;
      for (const step of next.steps) {
        if (!isStepResolved(step)) step.status = 'pending';
      }
      pushEvent('reassigned', { executorContactId: next.executorContactId });
      break;
    }

    case RUN_ACTIONS.RELEASE: {
      requireExecutor(c, 'release the run');
      next.executorContactId = null;
      next.executorUserId = null;
      next.acceptedAt = null;
      next.startedAt = null;
      // A released run goes back to the unassigned pool clean — any denial
      // by the departing executor no longer applies.
      next.deniedAt = null;
      for (const step of next.steps) {
        if (!isStepResolved(step)) step.status = 'pending';
      }
      pushEvent('released');
      break;
    }

    case RUN_ACTIONS.ARCHIVE: {
      // Archive-for-me (D1.2 applied to runs): owner and executor each hide
      // the run from their OWN view; nobody force-hides it from the other.
      // The global `archived` flag is derived (applyDerivedRunArchive).
      if (!c.isOwner && !c.isExecutor) {
        throw runActionError('permission-denied', 'Only the run owner or executor may archive the run');
      }
      if (!isRunSettled(next)) {
        throw runActionError('failed-precondition', 'Only settled runs (completed/flagged) can be archived');
      }
      // Self-runs (owner == executor) resolve as owner: one perspective.
      if (c.isOwner) {
        next.ownerArchived = true;
        // Stamp for the Archive screen's `orderBy ownerArchivedAt desc`
        // pagination (prd-task-run-archiving-2026-08).
        next.ownerArchivedAt = nowIso;
      } else {
        next.executorArchived = true;
        next.executorArchivedAt = nowIso;
      }
      applyDerivedRunArchive(next, nowIso);
      pushEvent('archived', { perspective: c.isOwner ? 'owner' : 'executor' });
      break;
    }

    case RUN_ACTIONS.UNARCHIVE: {
      // Reactivation clears the ACTOR's own flag and demotes the derived
      // global flag; the other party's personal flag survives, so the run
      // stays hidden for them. Restores visibility only: run state and steps
      // are untouched, so this can never bypass the execute/check funnels.
      if (!c.isOwner && !c.isExecutor) {
        throw runActionError('permission-denied', 'Only the run owner or executor may unarchive the run');
      }
      if (c.isOwner) {
        // Legacy docs (pre-D1.2) may carry archived:true without
        // ownerArchived — the owner may reactivate those too.
        if (next.ownerArchived !== true && next.archived !== true) {
          throw runActionError('failed-precondition', 'Run is not archived');
        }
        next.ownerArchived = false;
        next.ownerArchivedAt = null;
      } else {
        if (next.executorArchived !== true) {
          throw runActionError('failed-precondition', 'Run is not archived for the executor');
        }
        next.executorArchived = false;
        next.executorArchivedAt = null;
      }
      next.archived = false;
      next.archivedAt = null;
      next.archivedBy = null;
      pushEvent('unarchived');
      break;
    }

    default:
      throw runActionError('invalid-argument', `Unknown run action: ${a.type}`);
  }

  next.state = deriveRunState(next);
  next.failedCheckCount = countFailedChecks(next.steps);
  // SELF-HEAL (prd-workaround-remediation-2026-08, W4): active-set queries
  // filter these flags with equality, and Firestore equality filters never
  // match missing fields. Every interactive run write funnels through this
  // reducer, so a legacy doc missing a flag is healed on its next action.
  if (typeof next.archived !== 'boolean') next.archived = false;
  if (typeof next.ownerArchived !== 'boolean') next.ownerArchived = false;
  if (typeof next.executorArchived !== 'boolean') next.executorArchived = false;
  if (next.state === RUN_STATES.COMPLETED || next.state === RUN_STATES.FLAGGED) {
    if (!next.completedAt) next.completedAt = nowIso;
  } else {
    next.completedAt = null;
    // Archive-flag consistency (mirror of the tasks onTaskUpdated trigger):
    // list queries filter the personal flags at the QUERY level, so a run
    // that leaves the settled states (owner reopened a step / reassigned)
    // must not keep stale archive flags — they would hide a live run forever.
    if (next.ownerArchived === true || next.executorArchived === true || next.archived === true) {
      next.ownerArchived = false;
      next.ownerArchivedAt = null;
      next.executorArchived = false;
      next.executorArchivedAt = null;
      next.archived = false;
      next.archivedAt = null;
      next.archivedBy = null;
    }
  }
  next.updatedAt = nowIso;
  return next;
}

module.exports = {
  RUN_STATES,
  RESOLUTION_MODES,
  STEP_STATUSES,
  RESOLVED_STEP_STATUSES,
  NEGATIVE_STEP_STATUSES,
  CLEAN_STEP_STATUSES,
  STEP_POINTS_MIN,
  STEP_POINTS_MAX,
  DEFAULT_STEP_POINTS,
  RUN_ACTIONS,
  isStepResolved,
  isGatingStep,
  stepMode,
  runActionError,
  deriveRunState,
  countFailedChecks,
  computeRunProgress,
  isRunSettled,
  clampStepPoints,
  stepPointsEarned,
  computeRunScore,
  buildSectionsSnapshot,
  buildRunStep,
  isSelfAssignedRun,
  buildRunDoc,
  applyRunAction,
  normalizeVoiceNote,
  shouldPromoteRunArchive,
  findLatestResolutionEventIndex,
};
